import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Award, Rocket, Target, Star, Users, Briefcase } from 'lucide-react';
import { getApiBaseUrl } from '../../lib/apiBase';
import Seo from '../Seo';

const API_BASE_URL = getApiBaseUrl();

const MilestoneIcon = ({ title, index }) => {
  const text = (title || '').toLowerCase();
  if (text.includes('award') || text.includes('recognition')) return <Award className="w-6 h-6" />;
  if (text.includes('expansion') || text.includes('office')) return <Rocket className="w-6 h-6" />;
  if (text.includes('growth') || text.includes('growing')) return <Target className="w-6 h-6" />;
  if (text.includes('established') || text.includes('founded')) return <Star className="w-6 h-6" />;
  if (text.includes('client') || text.includes('partner')) return <Users className="w-6 h-6" />;
  
  const icons = [Award, Rocket, Target, Star, Users, Briefcase];
  const Icon = icons[index % icons.length];
  return <Icon className="w-6 h-6" />;
};

const Milestones = () => {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const timelineScrollerRef = useRef(null);
  const pauseUntilRef = useRef(0);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/milestones`)
      .then(async res => {
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Server status ${res.status}: ${text.substring(0, 30)}`);
        }
        return res.json();
      })
      .then(data => {
        setMilestones(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching milestones:', err);
        setLoading(false);
      });
  }, []);

  const orderedMilestones = Array.isArray(milestones)
    ? [...milestones].sort((a, b) => {
      const ay = Number(a.year);
      const by = Number(b.year);
      return ay - by;
    })
    : [];

  // Gentle auto-scroll so users don't need to manually drag horizontally.
  useEffect(() => {
    if (loading) return;

    const el = timelineScrollerRef.current;
    if (!el) return;
    if (el.scrollWidth <= el.clientWidth) return;

    // Start from the right edge so the newest years are visible first.
    el.scrollLeft = el.scrollWidth - el.clientWidth;

    const pxPerSecond = 18; // very slow, calm pace
    const pxPerMs = pxPerSecond / 1000;

    let rafId = 0;
    let lastTs = performance.now();

    const tick = (ts) => {
      rafId = requestAnimationFrame(tick);

      const node = timelineScrollerRef.current;
      if (!node) return;

      const shouldPause = Date.now() < pauseUntilRef.current;
      if (shouldPause) {
        lastTs = ts;
        return;
      }

      const dt = Math.max(0, ts - lastTs);
      lastTs = ts;

      // Move right-to-left automatically, without any jump back.
      node.scrollLeft = Math.max(0, node.scrollLeft - dt * pxPerMs);
      if (node.scrollLeft <= 0.5) {
        node.scrollLeft = 0;
        cancelAnimationFrame(rafId);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [loading, orderedMilestones.length]);

  return (
    <div className="bg-white min-h-screen">
      <Seo
        title="Milestones"
        description="Libsys Inc milestones — key moments, awards, client wins, and growth markers across our journey of delivering enterprise IT services and strategic staffing."
        path="/about-us/milestones"
      />
      <section className="pt-6 pb-8 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[22px] lg:text-[28px] font-black text-primary tracking-tight leading-tight"
          >
            Milestones (2002-2024)
          </motion.h1>
          <p className="mt-2 text-slate-600 text-tiny font-medium max-w-3xl mx-auto leading-relaxed">
            A year-by-year timeline reflecting long-term execution, partnerships, and responsible growth.
          </p>
        </div>
      </section>

      <section className="py-8 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="py-20 flex items-center justify-center">
              <div className="w-10 h-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            </div>
          ) : orderedMilestones.length > 0 ? (
            <div className="relative">
              {/* Horizontal dotted track behind milestone nodes */}
              <div className="absolute left-0 right-0 top-16 border-t-2 border-dashed border-secondary/30" />

              <div
                ref={timelineScrollerRef}
                onWheel={(e) => {
                  pauseUntilRef.current = Date.now() + 2500;
                  const el = timelineScrollerRef.current;
                  if (!el) return;
                  if (el.scrollWidth <= el.clientWidth) return;
                  // Map vertical wheel movement to horizontal scrolling for easier browsing.
                  if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) el.scrollLeft -= e.deltaY;
                }}
                onMouseEnter={() => {
                  pauseUntilRef.current = Date.now() + 1000000;
                }}
                onMouseLeave={() => {
                  pauseUntilRef.current = Date.now() + 2000;
                }}
                className="flex gap-6 overflow-x-auto scroll-smooth pb-10 pt-4"
              >
                {orderedMilestones.map((milestone, index) => (
                  <motion.div
                    key={milestone.id || `${milestone.year}-${index}`}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.04 }}
                    className="flex-shrink-0 w-[220px] sm:w-[240px] lg:w-[230px]"
                  >
                    {/* Node + vertical connector */}
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full border-4 border-secondary/60 bg-white flex items-center justify-center -mt-3 shadow-md">
                        <span className="text-secondary text-body font-black leading-none">
                          {String(orderedMilestones.length - index).padStart(2, '0')}
                        </span>
                      </div>
                      <span className="text-strategic-tagline text-tiny font-black uppercase tracking-widest mt-3">
                        Milestone
                      </span>
                      <div className="w-px h-7 border-l-2 border-dashed border-secondary/30 mt-2" />
                      <div className="w-12 h-12 rounded-full bg-white border border-secondary/20 flex items-center justify-center shadow-sm">
                        <MilestoneIcon title={milestone.title || milestone.year || ''} index={index} />
                      </div>
                    </div>

                    {/* Year card */}
                    <div className="mt-4 bg-white border border-slate-200 rounded-xl shadow-sm p-4 lg:p-5">
                      <p className="text-slate-900 text-[17px] font-black leading-none">{milestone.year}</p>
                      {milestone.title ? (
                        <p className="text-slate-700 text-tiny font-black mt-2 leading-relaxed">
                          {milestone.title}
                        </p>
                      ) : null}
 
                      {Array.isArray(milestone.bullets) && milestone.bullets.length > 0 ? (
                        <ul className="mt-1.5 pl-4 space-y-1.5 text-slate-600 text-tiny leading-relaxed font-medium list-disc">
                          {milestone.bullets.map((b, i) => (
                            <li key={`${milestone.year}-b-${i}`}>{b}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-slate-600 text-tiny leading-relaxed font-medium mt-1.5 whitespace-pre-line">
                          {milestone.description}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ) : (
            <div className="py-20 text-center">
              <p className="text-slate-600 font-medium">No milestones added yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* Organization summary */}      <section className="py-10 lg:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <p className="text-strategic-tagline text-[10px] font-black uppercase tracking-[0.1em] mb-2">
                Our Organization
              </p>
              <h2 className="text-[20px] lg:text-[24px] font-black text-primary leading-tight">
                Building long-term value through talent and technology
              </h2>
              <p className="mt-3 text-slate-600 text-tiny font-medium leading-relaxed">
                Libsys has grown by staying focused on what matters: delivering dependable programs and supporting enterprises with practical execution.
              </p>
              <div className="mt-4 grid sm:grid-cols-3 gap-3">
                <div>
                  <p className="text-strategic-tagline text-[10px] font-black uppercase tracking-[0.1em] mb-1.5">
                    Approach
                  </p>
                  <p className="text-slate-900 font-black text-small leading-tight">Governed execution</p>
                </div>
                <div>
                  <p className="text-strategic-tagline text-[10px] font-black uppercase tracking-[0.1em] mb-1.5">
                    Focus
                  </p>
                  <p className="text-slate-900 font-black text-small leading-tight">Client outcomes</p>
                </div>
                <div>
                  <p className="text-strategic-tagline text-[10px] font-black uppercase tracking-[0.1em] mb-1.5">
                    Value
                  </p>
                  <p className="text-slate-900 font-black text-small leading-tight">Reliable delivery</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
              <h3 className="text-body font-black text-slate-900">How we show up</h3>
              <ul className="mt-3 space-y-2 text-slate-600 text-tiny font-medium list-disc pl-5">
                <li>Clear governance and predictable delivery from discovery through implementation.</li>
                <li>Specialized capability in enterprise IT services and modern technology.</li>
                <li>Transparent communication that keeps stakeholders aligned.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>ction>

      {/* CTA */}
      <section className="py-12 bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 lg:p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5">
            <div>
              <p className="text-strategic-tagline text-[10px] font-black uppercase tracking-[0.1em] mb-2">Next Step</p>
              <h2 className="text-[20px] lg:text-[24px] font-black text-primary leading-tight">
                Explore our careers or contact Us
              </h2>
            </div>
            <div className="flex flex-wrap gap-2.5">
              <a
                href="/careers"
                className="px-6 py-2.5 bg-primary text-white font-black rounded-lg hover:bg-secondary transition-all text-small text-center"
              >
                Explore Openings
              </a>
              <a
                href="/about-us/contact-us"
                className="px-6 py-2.5 bg-white text-primary font-black border border-slate-200 rounded-lg hover:bg-slate-50 transition-all text-small text-center"
              >
                Contact
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Milestones;
